import React from "react";
import PropTypes from "prop-types";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../utils/classNames";
import DropdownMenu from "./DropdownMenu";

const Dropdown = React.forwardRef(
  ({
    value = [],
    onChange,
    options = [],
    placeholder = "Select...",
    multiple = false,
    disabled = false,
    className,
    icon,
    ...props
  }, ref) => {
    // Handle selection
    const handleSelect = (event, optionValue) => {
      if (multiple) {
        event.preventDefault(); // multi-select stays open
        const newValue = value.includes(optionValue)
          ? value.filter(v => v !== optionValue)
          : [...value, optionValue];
        onChange?.(newValue);
      } else {
        onChange?.([optionValue]);
      }
    };

    // Get display text
    const getDisplayText = () => {
      if (value.length === 0) return placeholder;
      if (value.length === 1) return value[0];
      return `${value.length} selected`;
    };

    // Check if option is selected
    const isSelected = (optionValue) => value.includes(optionValue);

    return (
      <div className={cn("relative", className)} {...props}>
        <DropdownMenu>
          <DropdownMenu.Trigger
            ref={ref}
            disabled={disabled}
            className={cn(
              "w-full flex items-center justify-between gap-2 control-height border border-border rounded-md",
              "bg-background/65 text-foreground text-left font-semibold leading-none text-button",
              "hover:bg-accent-gold/10 hover:border-accent-gold/70",
              "transition-colors duration-150",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-expanded:ring-2 aria-expanded:ring-primary/20 aria-expanded:border-primary",
              icon ? "pl-10 pr-4 py-3" : "px-4 py-3"
            )}
          >
            {icon && (
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                {icon}
              </div>
            )}
            <span className="truncate">{getDisplayText()}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 in-aria-expanded:rotate-180" />
          </DropdownMenu.Trigger>

          <DropdownMenu.Content fitTrigger>
            {options.length === 0 ? (
              <div className="px-4 py-2 text-body-small text-muted-foreground">
                No options available
              </div>
            ) : (
              <>
                {multiple && (
                  <DropdownMenu.Item
                    onClick={(event) => {
                      event.preventDefault(); // stays open
                      onChange?.([]);
                    }}
                    className="border-b border-border text-muted-foreground"
                  >
                    <div className="h-4 w-4" /> {/* Spacer for alignment */}
                    <span>Clear all</span>
                  </DropdownMenu.Item>
                )}
                {options.map((option) => {
                  const selected = isSelected(option);
                  return (
                    <DropdownMenu.Item
                      key={option}
                      role={multiple ? "menuitemcheckbox" : "menuitemradio"}
                      aria-checked={selected}
                      onClick={(event) => handleSelect(event, option)}
                    >
                      <div className="h-4 w-4 flex items-center justify-center">
                        {selected && <Check className="h-3 w-3" />}
                      </div>
                      <span className="truncate">{option}</span>
                    </DropdownMenu.Item>
                  );
                })}
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
    );
  }
);

Dropdown.displayName = "Dropdown";

Dropdown.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.node,
};

export default Dropdown;
